import { storeToRefs } from "pinia";

import loadScript from "shared/services/script-loader";
import { useGlobalStore } from "shared/stores/global";
import { useUserStore } from "shared/stores/user";
import type { Nullable } from "shared/types/utility";

declare global {
  interface Window {
    Intercom?: (...args: unknown[]) => void;
    intercomSettings?: Record<string, unknown>;
    pendo: {
      clearSession?: () => void;
      identify: (options: PendoInitializeOptions) => void;
      initialize: (options: PendoInitializeOptions) => void;
      pageLoad: () => void;
      track: (event: string, metadata?: Record<string, unknown>) => void;
      trackAgent: (event: string, metadata?: Record<string, unknown>) => void;
      updateOptions: (options: PendoInitializeOptions) => void;
    };
  }
}

interface PendoVisitor {
  [key: string]: unknown;
  email: string;
  full_name: string;
  id: string;
}

interface PendoAccount {
  global_customer_id?: string;
  id?: string;
  name: string;
}

interface PendoInitializeOptions {
  account?: PendoAccount;
  visitor?: PendoVisitor;
}

let pendoInitialized = false;

// Exported for testing purposes only — do not call in production code
export function resetPendoInitialized(): void {
  pendoInitialized = false;
}

interface PendoAttributes {
  [key: string]: unknown;
  contract_services?: string[];
  email: string;
  first_name: string;
  global_customer_id?: string | number;
  has_instant_insights?: boolean;
  has_monitor?: boolean;
  has_outreach?: boolean;
  has_social?: boolean;
  id: string | number;
  last_name: string;
  organisation_id: string | number;
  organisation_name: string;
  role?: string;
  ui_language?: Nullable<string>;
  user_created_at?: Nullable<string>;
}

export default async function initPendo(
  pendoAttributes: PendoAttributes
): Promise<void> {
  const globalStore = useGlobalStore();
  const { appConfig } = storeToRefs(globalStore);

  const publicAppId = appConfig.value.pendo?.publicAppId;

  if (
    !publicAppId ||
    !/^(production|preprod)_[a-z]{2}$/.test(import.meta.env.VITE_ENV)
  ) {
    return;
  }

  const { organisation } = useUserStore();
  const { features } = organisation;

  await mountPendo(publicAppId);

  identifyUser({
    ...pendoAttributes,
    has_instant_insights: features.includes("has_instant_insights"),
    has_monitor: features.includes("has_media_platform"),
    has_outreach: features.includes("has_outreach"),
    has_social: features.includes("has_social_platform"),
  });

  const originalIntercom = window.Intercom;

  window.Intercom = function IntercomWrapper(...args: any) {
    const command = args[0];
    const options = args[1];

    if (originalIntercom) {
      originalIntercom.call(window, command, options);
    }

    window.intercomSettings = window.intercomSettings || {};
    window.intercomSettings.alignment = "left";
  };
}

export function identifyUser(attributes: PendoAttributes): void {
  const {
    email,
    first_name: firstName,
    global_customer_id: globalCustomerId,
    id: unusedId,
    last_name: lastName,
    organisation_id: unusedOrganisationId,
    organisation_name: organisationName,
    role,
    ...rest
  } = attributes;

  const hasGlobalCustomerId =
    globalCustomerId !== null && globalCustomerId !== undefined;

  const options = {
    account: {
      ...(hasGlobalCustomerId && {
        global_customer_id: String(globalCustomerId),
        id: String(globalCustomerId),
      }),
      name: organisationName,
    },
    visitor: {
      email,
      full_name: `${firstName} ${lastName}`,
      id: email,
      ...(role !== undefined && { monitor_role: role }),
      ...rest,
    },
  };

  if (pendoInitialized) {
    window.pendo.identify(options);
  } else {
    window.pendo.initialize(options);
    pendoInitialized = true;
  }
}

export function mountPendo(publicAppId: string): Promise<Event> {
  return loadScript(
    `https://cdn.pendo.io/agent/static/${publicAppId}/pendo.js`
  );
}

export function clearPendoSession(): void {
  window.pendo?.clearSession?.();
}
